import React, { ChangeEvent, TextareaHTMLAttributes, forwardRef } from 'react'
import { Ref } from 'react'
import clsx from 'clsx'

import { isWindow } from 'helpers/common'

export interface TextareaInputProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * What error to perform ?
   */
  isError?: boolean
  /**
   * What message to perform ?
   */
  message?: string
  /**
   * Optional character count
   */
  hasCountable?: boolean
  /**
   * Optional resize width
   */
  fullWidth?: boolean
  /**
   * Optional change handler
   */
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void
}

/**
 * Primary UI component for user interaction
 */

function TextareaInput(
  props: TextareaInputProps,
  ref: Ref<HTMLTextAreaElement>,
): JSX.Element {
  const {
    className,
    isError = false,
    disabled = false,
    fullWidth = false,
    value,
    maxLength = 1000,
    message,
    hasCountable = false,
    onChange = () => {},
    ...others
  } = props

  const cn = clsx(
    'textarea-input',
    disabled && 'textarea-input--disabled',
    className,
  )

  const textareaProps = {
    ...others,
    ref,
    className: cn,
    maxLength,
  }

  const base = 'textarea-input'
  const currentCount = typeof value === 'string' ? value.length : 0

  return (
    <div
      className={clsx(
        `${base}__wrapper`,
        fullWidth && `${base}__wrapper--full-width`,
      )}
      data-testid='textarea-input'
    >
      <div
        className={clsx(`${base}__container`)}
        data-testid='textarea-input-container'
      >
        <div
          className={clsx(
            `${base}__textarea-layout`,
            isError && `${base}__textarea-layout--required`,
            disabled && `${base}__textarea-layout--disabled`,
          )}
          data-testid='textarea-layout'
        >
          <textarea
            {...textareaProps}
            onChange={(e) => {
              onChange(e)
            }}
            value={value}
            disabled={disabled}
            data-testid='textarea-input-element'
          />
          {hasCountable && (
            <span
              className={clsx(
                `${base}__countable`,
                isWindow && `${base}__countable--window`,
                currentCount >= maxLength && `${base}__countable--danger`,
              )}
              data-testid='textarea-input-countable'
            >
              {currentCount}/{maxLength || 0}
            </span>
          )}
        </div>
      </div>
      {message && (
        <span
          className={clsx(
            `${base}__message`,
            isError && `${base}__message--danger`,
          )}
          data-testid='textarea-input-message'
        >
          {message}
        </span>
      )}
    </div>
  )
}

export default forwardRef(TextareaInput)
